import { Menu, MenuPopper } from '@zillow/constellation';
import React, { useCallback } from 'react';
import { Bedrooms } from 'app/shared/types/Bedrooms';
import { PropertyTypes } from 'app/shared/types/PropertyTypes';

import * as S from './styles';

import type { CSSObject } from 'styled-components';

const logger = getLogger('components/FilterTray');

interface FilterTrayProps {
  bedrooms: Bedrooms;
  setBedrooms: (bedrooms: Bedrooms) => (dispatch: any, getState: any) => Promise<void>;
  propertyTypes: PropertyTypes;
  setPropertyTypes: (propertyTypes: PropertyTypes) => (dispatch: any, getState: any) => Promise<void>;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  isMobile: boolean;
  style?: CSSObject;
}

const FilterTray: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<FilterTrayProps>>> = (
  props,
) => {
  const { bedrooms, setBedrooms, propertyTypes, setPropertyTypes, loading, setLoading, isMobile, style } = props;

  const changeBedrooms = useCallback(
    (newBedrooms: Bedrooms) => {
      setLoading(true);
      // @ts-ignore
      setBedrooms(newBedrooms)
        .then(() => {
          setLoading(false);
          return;
        })
        .catch((error) => {
          logger.error('Error while changing bedrooms filter', error);
          setLoading(false);
        });
    },
    [setBedrooms, setLoading],
  );

  const changePropertyTypes = useCallback(
    (newPropertyTypes: PropertyTypes) => {
      setLoading(true);
      // @ts-ignore
      setPropertyTypes(newPropertyTypes)
        .then(() => {
          setLoading(false);
          return;
        })
        .catch((error) => {
          logger.error('Error while changing propertyType filter', error);
          setLoading(false);
        });
    },
    [setPropertyTypes, setLoading],
  );

  const selectAllBedrooms = useCallback(() => changeBedrooms(Bedrooms.ALL), [changeBedrooms]);
  const selectStudio = useCallback(() => changeBedrooms(Bedrooms.STUDIO), [changeBedrooms]);
  const selectOneBedroom = useCallback(() => changeBedrooms(Bedrooms.ONE), [changeBedrooms]);
  const selectTwoBedrooms = useCallback(() => changeBedrooms(Bedrooms.TWO), [changeBedrooms]);
  const selectThreeBedrooms = useCallback(() => changeBedrooms(Bedrooms.THREE), [changeBedrooms]);
  const selectFourPlusBedrooms = useCallback(() => changeBedrooms(Bedrooms.FOURPLUS), [changeBedrooms]);

  const selectAllPropertyTypes = useCallback(() => changePropertyTypes(PropertyTypes.ALL), [changePropertyTypes]);
  const selectHouses = useCallback(() => changePropertyTypes(PropertyTypes.HOUSE), [changePropertyTypes]);
  const selectApartments = useCallback(() => changePropertyTypes(PropertyTypes.APTSANDCONDOS), [changePropertyTypes]);
  const selectTownhouses = useCallback(() => changePropertyTypes(PropertyTypes.TOWNHOUSE), [changePropertyTypes]);

  return (
    <S.Container style={style}>
      <MenuPopper
        triggered={
          <Menu>
            <S.MenuItem isSelected={bedrooms === Bedrooms.ALL} onClick={selectAllBedrooms}>
              All bedrooms
            </S.MenuItem>
            <S.MenuItem isSelected={bedrooms === Bedrooms.STUDIO} onClick={selectStudio}>
              Studio
            </S.MenuItem>
            <S.MenuItem isSelected={bedrooms === Bedrooms.ONE} onClick={selectOneBedroom}>
              1 bedroom
            </S.MenuItem>
            <S.MenuItem isSelected={bedrooms === Bedrooms.TWO} onClick={selectTwoBedrooms}>
              2 bedroom
            </S.MenuItem>
            <S.MenuItem isSelected={bedrooms === Bedrooms.THREE} onClick={selectThreeBedrooms}>
              3 bedroom
            </S.MenuItem>
            <S.MenuItem isSelected={bedrooms === Bedrooms.FOURPLUS} onClick={selectFourPlusBedrooms}>
              4 bedroom +
            </S.MenuItem>
          </Menu>
        }
        // @ts-ignore
        marginRight="xs"
      >
        <S.DropdownButton fontType="bodySmallHeading" disabled={loading}>
          {bedrooms}
        </S.DropdownButton>
      </MenuPopper>
      <MenuPopper
        triggered={
          <Menu>
            <S.MenuItem isSelected={propertyTypes === PropertyTypes.ALL} onClick={selectAllPropertyTypes}>
              All property types
            </S.MenuItem>
            <S.MenuItem isSelected={propertyTypes === PropertyTypes.HOUSE} onClick={selectHouses}>
              Houses
            </S.MenuItem>
            <S.MenuItem isSelected={propertyTypes === PropertyTypes.APTSANDCONDOS} onClick={selectApartments}>
              Apartments and Condos
            </S.MenuItem>
            <S.MenuItem isSelected={propertyTypes === PropertyTypes.TOWNHOUSE} onClick={selectTownhouses}>
              Townhomes
            </S.MenuItem>
          </Menu>
        }
      >
        <S.DropdownButton fontType="bodySmallHeading" disabled={loading}>
          {propertyTypes === PropertyTypes.APTSANDCONDOS && isMobile ? 'Apts. & Condos' : propertyTypes}
        </S.DropdownButton>
      </MenuPopper>
    </S.Container>
  );
};

export default FilterTray;
